<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useOrganizationStore } from '@/stores/organizationStore'
import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()

const route = useRoute()
const organizationId = route.params.id
const orgStore = useOrganizationStore()

// State
const isLoading = ref(false)
const isAdding = ref(false)
const errorMsg = ref('')

// Available Roles Mapping (Display Name -> Backend Value)
const availableRoles = [
  { label: 'Admin', value: 'organization_admin' },
  { label: 'Developer', value: 'organization_developer' },
  { label: 'Analyst', value: 'organization_analyst' },
  { label: 'Billing Admin', value: 'organization_billing_admin' },
  { label: 'Member', value: 'organization_member' }
]

// Form State
const newMemberRole = ref('organization_developer')
const newMemberEmail = ref('')
const newMemberName = ref('')

// Access users from store
const users = computed(() => orgStore.users)

// Fetch Members
const fetchUsers = async () => {
  isLoading.value = true
  try {
    await orgStore.fetchUsers(organizationId)
  } catch (err) {
    console.error('Failed to fetch users:', err)
  } finally {
    isLoading.value = false
  }
}

// Add Member
const handleAddMember = async () => {
  if (!newMemberEmail.value || !newMemberName.value) return

  isAdding.value = true
  errorMsg.value = ''

  try {
    const memberData = {
      email: newMemberEmail.value,
      name: newMemberName.value,
      // 'type' might be optional if the endpoint is strictly for users,
      // but sending it usually doesn't hurt.
      type: 'user',
      meta: {
        assignRole: newMemberRole.value
      }
    }

    await orgStore.addUser(organizationId, memberData)

    // Clear form
    newMemberEmail.value = ''
    newMemberName.value = ''
    newMemberRole.value = 'organization_developer'
  } catch (err) {
    errorMsg.value = err.response?.data?.error?.message || 'Failed to add member'
  } finally {
    isAdding.value = false
  }
}

// Remove Member
const handleRemoveMember = async (member) => {
  if (!confirm(`Are you sure you want to remove ${member.name || member.email}?`)) return

  try {
    // FIX: Use simple ID for the /users endpoint
    await orgStore.removeUser(organizationId, member.id)
  } catch (err) {
    console.error('Failed to remove member:', err)
    alert('Failed to remove member')
  }
}

// Helper to display roles nicely
const formatRole = (role) => {
  if (!role) return 'Member'
  const cleanRole = role.replace('organization_', '')
  return cleanRole.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

onMounted(() => {
  fetchUsers()
})
</script>

<template>
  <div class="space-y-12">
    <div class="border-b border-b-gray-100 pb-4">
      <h1 class="text-xl text-gray-900">Users</h1>
      <p class="text-md font-light text-gray-500 mt-1">Manage users and their roles in organization</p>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="px-6 py-5 border-b border-gray-100 flex justify-between items-center">
        <h2 class="text-lg text-gray-900">Users</h2>
        <span v-if="isLoading" class="text-xs text-gray-400">Loading...</span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead>
            <tr class="border-b border-gray-100">
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Organization role
              </th>
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Email
              </th>
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Name
              </th>
              <th class="px-6 py-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="users.length === 0 && !isLoading">
              <td colspan="4" class="px-6 py-8 text-center text-gray-500">
                No users found.
              </td>
            </tr>
            <tr v-for="member in users" :key="member.id"
              class="bg-white hover:bg-gray-50/50 transition border-b border-gray-50 last:border-0">
              <td class="px-6 py-2 font-medium text-gray-900">
                {{ formatRole(member.meta?.role) }}
              </td>
              <td class="px-6 py-2 font-medium text-gray-900">{{ member.email }}</td>
              <td class="px-6 py-2 font-medium text-gray-900">{{ member.name }}</td>
              <td class="px-6 py-2 text-right">
                <div class="inline-flex items-center gap-x-1">
                  <button @click="handleRemoveMember(member)" v-if="member.email !== authStore.user.email"
                    class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded transition"
                    :title="`Remove ${member.email} from organization`">
                    <svg class=" w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clip-rule="evenodd"></path>
                    </svg>
                  </button>
                  <div v-else class="p-1.5">&nbsp;</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="p-4 bg-gray-50 border-t border-gray-100">
        <p v-if="errorMsg" class="mb-2 text-xs text-red-600 font-medium px-1">{{ errorMsg }}</p>

        <form @submit.prevent="handleAddMember" class="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <select v-model="newMemberRole"
            class="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black h-9 sm:min-w-42">
            <option v-for="role in availableRoles" :key="role.value" :value="role.value">
              {{ role.label }}
            </option>
          </select>
          <input v-model="newMemberEmail" type="email" placeholder="Email" required
            class="grow px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black h-9" />
          <input v-model="newMemberName" type="text" placeholder="Name" required
            class="grow px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black h-9" />

          <button type="submit" :disabled="isAdding"
            class="bg-black text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition flex items-center h-9 disabled:opacity-70 disabled:cursor-not-allowed">
            <svg v-if="isAdding" class="animate-spin -ml-1 mr-2 h-3.5 w-3.5 text-white"
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            <svg v-else class="w-3.5 h-3.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 4v16m8-8H4" />
            </svg>
            Add member
          </button>
        </form>
      </div>
    </div>
  </div>
</template>